import { useEffect, useState } from "react";
import { useTenantMe, useUpdateTenantMe } from "@/lib/tenant-me";
import { CURRENCIES } from "@/lib/currency";
import { TIMEZONES } from "@/lib/timezones";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/v2/Toast";
import { SettingsSection } from "@/components/v2/account/SettingsSection";

/**
 * `/workspace/regional` — currency + timezone for the workspace.
 * Drives currency formatting on bookings and the timezone bookings
 * are recorded against. Country is a read-only signup artifact.
 */
export function WorkspaceRegional() {
  const me = useTenantMe();
  const update = useUpdateTenantMe();
  const toast = useToast();
  const t = me.data?.tenant;

  const [currency, setCurrency] = useState("PHP");
  const [timezone, setTimezone] = useState("Asia/Manila");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (t) {
      setCurrency(t.currency ?? "PHP");
      setTimezone(t.timezone ?? "Asia/Manila");
    }
  }, [t]);

  const dirty =
    currency !== (t?.currency ?? "PHP") ||
    timezone !== (t?.timezone ?? "Asia/Manila");
  const valid = currency.length === 3;
  const canSave = dirty && valid && !submitting;

  const onSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      await update.mutateAsync({
        currency: currency.toUpperCase(),
        timezone,
      });
      toast.success("Regional settings updated");
    } catch (e) {
      toast.error("Couldn't save", (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Region"
        description="Drives currency formatting + timezone for bookings."
      >
        <div className="grid gap-4 md:grid-cols-3">
          <div className="grid gap-1.5">
            <Label htmlFor="ws-country" className="text-[13px] font-semibold">
              Country
            </Label>
            <Input id="ws-country" value={t?.countryIso2 ?? "—"} readOnly />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ws-currency" className="text-[13px] font-semibold">
              Currency <span className="text-destructive">*</span>
            </Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger id="ws-currency" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.iso} value={c.iso}>
                    <span className="inline-flex items-center gap-2">
                      <span className="font-semibold w-5 text-center">
                        {c.symbol}
                      </span>
                      <span>{c.name}</span>
                      <span className="text-muted-foreground text-xs">
                        ({c.iso})
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ws-tz" className="text-[13px] font-semibold">
              Timezone
            </Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="ws-tz" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </SettingsSection>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={!canSave}>
          {submitting ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
